import * as d3 from 'd3';
import { NumberValue, } from 'd3';
import {Selection, ScaleContinuousNumeric, ScaleSequentialBase, ScaleDiverging, ScaleQuantize, ScaleQuantile, ScaleThreshold, ScaleOrdinal,InterpolatorFactory} from 'd3';
import { markup2element } from '../dom/dom';


//type ColorScale=    <Range, Unknown = never>(value: NumberValue) => Range | Unknown;
//
//type LegendColor= ScaleContinuousNumeric<?,?,?>  & { interpolate: ()=> InterpolatorFactory<any, any> } |
//ScaleSequentialBase<?,?> |
//ScaleDiverging<?,?> |
//ScaleQuantize<?,?> |
//ScaleQuantile<?,?> |
//ScaleThreshold<?,?,?> |
//ScaleOrdinal<?,?,?>;
type LegendColor= any;


//type LegendColor = ScaleLinear<?,?,?> |
//ScalePower<?,?,?> |
//ScaleLogarithmic<?,?,?> |
//ScaleSymLog<?,?,?> |
//ScaleSequentialBase<?,?> |
//ScaleDiverging<?,?> |
//ScaleQuantize<?,?> |
//ScaleQuantile<?,?> |
//ScaleThreshold<?,?,?> |
//ScaleOrdinal<?,?,?>
//
//const color = d3.scaleOrdinal()
//        .range(["#98abc5", "#8a89a6", "#7b6888", "#6b486b", "#a05d56", "#d0743c", "#ff8c00"])


interface LegendOptions {
  title? : string,
  tickSize?: number,
  width?:number, 
  height?: number
  marginTop?: number,
  marginRight?: number,
  marginBottom?: number,
  marginLeft?: number,
  ticks?: number,
  tickFormat?: string | ((n: number | { valueOf(): number }) => string),
  tickValues?: number[] | string[]
}



// Copyright 2021, Observable Inc.
// Released under the ISC license.
// https://observablehq.com/@d3/color-legend
export function Legend(color: LegendColor  , {
  title,
  tickSize = 6,
  width = 320, 
  height = 44 + tickSize,
  marginTop = 18,
  marginRight = 0,
  marginBottom = 16 + tickSize,
  marginLeft = 0,
  ticks = width / 64,
  tickFormat,
  tickValues
}: LegendOptions = {}): SVGSVGElement  {

  function ramp(color: LegendColor , n = 256) {
    const canvas = document.createElement("canvas");
    canvas.width = n;
    canvas.height = 1;
    const context = canvas.getContext("2d");
    for (let i = 0; i < n; ++i) {
      context.fillStyle = color(i / (n - 1));
      context.fillRect(i, 0, 1, 1);
    }
    return canvas;
  }

  const svg = d3.create("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .style("overflow", "visible")
      .style("display", "block");

  let tickAdjust: (selection:Selection<any,any,any,any>, ...args: any[]) => void  = g => g.selectAll(".tick line").attr("y1", marginTop + marginBottom - height);
  let x:any;

  // Continuous
  if (color.interpolate) {
    const n = Math.min(color.domain().length, color.range().length);

    x = color.copy().rangeRound(d3.quantize(d3.interpolate(marginLeft, width - marginRight), n));

    svg.append("image")
        .attr("x", marginLeft)
        .attr("y", marginTop)
        .attr("width", width - marginLeft - marginRight)
        .attr("height", height - marginTop - marginBottom)
        .attr("preserveAspectRatio", "none")
        .attr("xlink:href", ramp(color.copy().domain(d3.quantize(d3.interpolate(0, 1), n))).toDataURL());
  }

  // Sequential
  else if (color.interpolator) {
    x = Object.assign(color.copy()
        .interpolator(d3.interpolateRound(marginLeft, width - marginRight)),
        {range() { return [marginLeft, width - marginRight]; }});

    svg.append("image")
        .attr("x", marginLeft)
        .attr("y", marginTop)
        .attr("width", width - marginLeft - marginRight)
        .attr("height", height - marginTop - marginBottom)
        .attr("preserveAspectRatio", "none")
        .attr("xlink:href", ramp(color.interpolator()).toDataURL());

    // scaleSequentialQuantile doesn’t implement ticks or tickFormat.
    if (!x.ticks) {
      if (tickValues === undefined) {
        const n = Math.round(ticks + 1);
        tickValues = d3.range(n).map(i => d3.quantile(color.domain(), i / (n - 1)));
      }
      if (typeof tickFormat !== "function") {
        tickFormat = d3.format(tickFormat === undefined ? ",f" : <string>tickFormat);
      }
    }
  }

  // Threshold
  else if (color.invertExtent) {
    const thresholds: number[]
        = color.thresholds ? color.thresholds() // scaleQuantize
        : color.quantiles ? color.quantiles() // scaleQuantile
        : color.domain(); // scaleThreshold

    const thresholdFormat: (n: number | { valueOf(): number }, index?: number) => string
        = tickFormat === undefined ? (d: number) => ''+d
        : typeof tickFormat === "string" ? d3.format(tickFormat)
        : tickFormat;

    x = d3.scaleLinear()
        .domain([-1, color.range().length - 1])
        .rangeRound([marginLeft, width - marginRight]);

    svg.append("g")
      .selectAll("rect")
      .data(color.range())
      .join("rect")
        .attr("x", (d, i) => x(i - 1))
        .attr("y", marginTop)
        .attr("width", (d, i) => x(i) - x(i - 1))
        .attr("height", height - marginTop - marginBottom)
        .attr("fill", (d: any) => d);

    tickValues = d3.range(thresholds.length);
//    tickFormat = i => thresholdFormat(thresholds[i], i);

    tickFormat = (i:number) => thresholdFormat(thresholds[i], i);
    
  }

  // Ordinal
  else {
    x = d3.scaleBand()
        .domain(color.domain())
        .rangeRound([marginLeft, width - marginRight]);

    svg.append("g")
      .selectAll("rect")
      .data(color.domain())
      .join("rect")
        .attr("x", x)
        .attr("y", marginTop)
        .attr("width", Math.max(0, x.bandwidth() - 1))
        .attr("height", height - marginTop - marginBottom)
        .attr("fill", color);

    tickAdjust = () => {};
  }

  svg.append("g")
      .attr("transform", `translate(0,${height - marginBottom})`)
      .call(d3.axisBottom(x)
        .ticks(ticks, typeof tickFormat === "string" ? tickFormat : undefined)
        .tickFormat(typeof tickFormat === "function" ? tickFormat : undefined)
        .tickSize(tickSize)
        .tickValues(tickValues))
      .call(tickAdjust)
      .call(g => g.select(".domain").remove())
      .call(g => g.append("text")
        .attr("x", marginLeft)
        .attr("y", marginTop + marginBottom - height - 6)
        .attr("fill", "currentColor")
        .attr("text-anchor", "start")
        .attr("font-weight", "bold")
        .attr("class", "title")
        .text(title));

  return svg.node();
}


//Legend(d3.scaleSequential([0, 100], d3.interpolateViridis), {
//  title: "Temperature (°F)"
//})
//
//Legend(d3.scaleThreshold([2.5, 3.1, 3.5, 3.9, 6, 7, 8, 9.5], d3.schemeRdBu[9]), {
//  title: "Unemployment rate (%)",
//  tickSize: 0
//})
//
//Legend(d3.scaleQuantize([1, 10], d3.schemePurples[9]), {
//  title: "Unemployment rate (%)"
//})
//
//Legend(d3.scaleQuantile(d3.range(1000).map(d3.randomNormal(100, 20)), d3.schemeSpectral[9]), {
//  title: "Height (cm)",
//  tickFormat: ".0f"
//})
//Legend(d3.scaleSequentialLog([1, 100], d3.interpolateBlues), {
//  title: "Energy (joules)",
//  ticks: 10
//})
